import * as React from 'react';

import { Filter } from 'lucide-react';

import { ExpressionInput } from '../../../shared/ExpressionInput';
import { NodeConfigProps } from '../../types';

import { FilterConfigType } from './schema';

/**
 * Configuration panel for Filter nodes.
 * Allows configuring filter expression that determines if flow continues.
 */
export const FilterConfig: React.FC<NodeConfigProps<FilterConfigType>> = ({
  config,
  onUpdate,
}) => {
  const [expression, setExpression] = React.useState<string>(
    config.expression || '',
  );

  const handleUpdate = React.useCallback(
    (updates: Partial<FilterConfigType>) => {
      const newConfig = { ...config, ...updates };
      onUpdate(newConfig);
    },
    [config, onUpdate],
  );

  const handleExpressionChange = React.useCallback(
    (value: string) => {
      setExpression(value);
      handleUpdate({ expression: value });
    },
    [handleUpdate],
  );

  return (
    <div className="@FilterConfig space-y-4">
      <div>
        <ExpressionInput
          label="Filter Expression"
          value={expression}
          onChange={handleExpressionChange}
          placeholder="trigger.data.status == 'active'"
          description="Flow continues only if this expression evaluates to true"
        />
      </div>

      <div className="rounded-lg border bg-neutral/5 p-3">
        <p className="mb-2 text-xs font-medium">Expression Examples:</p>
        <div className="space-y-1 font-mono text-xs text-neutral/70">
          <div>
            <code>trigger.data.amount &gt; 1000</code>
          </div>
          <div>
            <code>trigger.data.status == "approved"</code>
          </div>
          <div>
            <code>len(trigger.data.items) &gt; 0</code>
          </div>
          <div>
            <code>trigger.data.priority == "high" && trigger.data.urgent</code>
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-warning/20 bg-warning/10 p-3">
        <div className="flex items-start gap-2">
          <Filter className="mt-0.5 size-4 shrink-0 text-warning" />
          <div>
            <p className="text-xs font-medium text-warning">Unlike Condition</p>
            <p className="mt-1 text-xs text-warning">
              Filter has no false branch. If the expression evaluates to false,
              the flow simply stops at this point.
            </p>
          </div>
        </div>
      </div>

      <div className="rounded-lg border bg-neutral/5 p-3">
        <p className="mb-2 text-xs font-medium">Common Use Cases:</p>
        <div className="space-y-1 text-xs text-neutral/70">
          <div>• Process only high-value orders (amount &gt; 1000)</div>
          <div>• Continue only for approved requests</div>
          <div>• Filter out empty results</div>
          <div>• Verify required fields are present</div>
        </div>
      </div>

      <div className="rounded-lg border border-primary/20 bg-primary/10 p-3">
        <p className="text-xs text-primary">
          <strong>Tip:</strong> Access trigger data with{' '}
          <code className="rounded bg-primary/20 px-1">
            trigger.data.fieldName
          </code>{' '}
          and previous action outputs with{' '}
          <code className="rounded bg-primary/20 px-1">
            outputs.nodeId.fieldName
          </code>
          .
        </p>
      </div>

      <div className="rounded-lg border border-warning/20 bg-warning/10 p-3">
        <p className="text-xs text-warning">
          <strong>Operators:</strong> Use ==, !=, {'<'}, {'>'}, {'<='}, {'>='},
          &&, ||, ! for comparisons. Use len() for array/string length.
        </p>
      </div>
    </div>
  );
};
